import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

// Counterpart of Ui::SelectionButtonComponent.
//
// `style` is purely visual — selection logic lives with whoever owns the
// selection. Standalone the controller owns it and the button toggles freely
// (a native radio can only be picked, never unticked). When `controlled` is
// set — i.e. inside Ui::SelectionButtonGroupComponent — clicks only dispatch
// the intent and the group's `behaviour` decides.
export default class extends Controller {
  static targets = ['radio', 'hidden'];

  static values = {
    selected: Boolean,
    style: { type: String, default: 'simple' },
    value: String,
    name: String,
    autoSaveUrl: String,
    controlled: Boolean,
    debounce: { type: Number, default: 0 }
  };

  connect() {
    this.connected = true;
    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.nameValue,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element
    });
  }

  disconnect() {
    this.connected = false;
    this.autoSave.cancel();
  }

  // ── Public actions (wired from data-action) ──────────────────────────────────
  handleClick(event) {
    if (this.element.disabled) return;

    if (this.isRadio) {
      // The radio itself bubbles its click back up here — the change handler
      // already covered it.
      if (event.target instanceof HTMLInputElement) return;
      this.radioTarget.click();
      return;
    }

    this.commit(!this.selectedValue);
  }

  handleRadioChange(event) {
    this.commit(event.target.checked);
  }

  // ── Group interface ─────────────────────────────────────────────────────────
  // Called by selection-button-group-component, which owns selection while
  // `controlled`.
  setSelected(next) {
    this.selectedValue = next;
  }

  get optionValue() {
    return this.valueValue;
  }

  // ── Internals ───────────────────────────────────────────────────────────────
  commit(next) {
    if (!this.controlledValue) this.selectedValue = next;

    this.dispatch('change', { detail: { value: this.valueValue, selected: next } });

    if (this.shouldAutoSave(next)) this.autoSave.save(this.valueValue);
  }

  // Toggling off never persists, and a controlled button leaves saving to the
  // group so the whole selection is sent as one payload.
  shouldAutoSave(next) {
    return !this.controlledValue && this.autoSaveUrlValue !== '' && next && this.hasValueValue;
  }

  // The single place the DOM is brought in line with the state — Stimulus runs
  // it for standalone commits and for the group's setSelected alike.
  selectedValueChanged(selected) {
    this.element.classList.toggle('ui-selection-button--selected', selected);
    if (!this.isRadio) this.element.setAttribute('aria-pressed', String(selected));
    if (this.hasRadioTarget) this.radioTarget.checked = selected;
    if (!this.hasHiddenTarget) return;

    this.hiddenTarget.disabled = !selected;

    // A hidden input does not announce itself. Only the newly picked button speaks, and only
    // after connect — the first call carries server-rendered state, not a choice.
    if (this.connected && selected) {
      this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  get isRadio() {
    return this.styleValue === 'radio';
  }
}
